/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import {
  IconDotsVertical,
  IconDownload,
  IconPlus,
  IconX,
  IconSearch,
  IconUpload,
} from "@tabler/icons-react";
import ToggleButton from "../../../components/ToggleButton";
import AssignModal from "./AssignModal";
import { useForm } from "react-hook-form";

import {
  useUpdateVehicleMutation,
  useUpdateVehicleStatus,
  useVehicleAssignMutation,
  useVehicleCreateMutation,
  useVehicleListData,
  useFetchFleetCardsSummary,
} from "../../../apis/fleetApis";
import toast from "react-hot-toast";
import NoDataFound from "../../../components/common/NoDataFound";
import Pagination from "../../../components/common/Pagination";
import useDebounce from "../../../utilities/useDebounce";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { useSelector } from "react-redux";
import { handleDownload } from "../../../utilities/downloadHelper";
import { usePermission } from "../../../hooks/usePermissionTabs";
import CommonModal from "../../../components/common/CommonModal";
import VehicleCardSkeleton from "../../../components/skeleton/VehicleCardSkeleton";
import { T, useLanguage } from "../../../context/LanguageContext";

const getStatusClass = (status: string) => {
  switch (status) {
    case "NOT_ASSIGNED":
      return "border-[#FF4848] bg-[#FF4848]/20";
    case "ASSIGNED":
      return "border-[#20BF55] bg-[#20BF55]/20";
    default:
      return "";
  }
};

const formatStatus = (status: string) => {
  return status
    ?.toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getStatusBadgeStyles = (status: string) => {
  switch (status) {
    case "ACTIVE":
    case "AVAILABLE":
      return "bg-emerald-500/15 text-emerald-600 border-emerald-500/20";
    case "INACTIVE":
      return "bg-slate-500/15 text-slate-600 border-slate-500/20";
    case "ASSIGNED":
      return "bg-blue-500/15 text-blue-600 border-blue-500/20";
    case "MAINTENANCE":
      return "bg-amber-500/15 text-amber-600 border-amber-500/20";
    case "ACCIDENT":
      return "bg-rose-500/15 text-rose-600 border-rose-500/20";
    case "BREAKDOWN":
      return "bg-violet-500/15 text-violet-600 border-violet-500/20";
    default:
      return "bg-gray-500/15 text-gray-600 border-gray-500/20";
  }
};

export type FormValues = {
  id: string;
  name: string;
  vehicleType: string;
  rcNumber: string;
  fuelType: string;
  model: string;
  vehicleNumber: string;
  odometer: string;
  year: string;
  document: string;
  status?: string;
  images?: string;
  imagesOdoMeter?: string;
  rcDocs?: string;
  makeId?: string;
  makeName?: string;
  modelId?: string;
  modelName?: string;

  registrationType?: string;
  color?: string;
  sequenceNumber?: string;
  inspectionExpiryDate?: string;
  registrationExpiryDate?: string;
  insuranceCompany?: string;
  insuranceStartDate?: string;
  insuranceEndDate?: string;
  inspectionPaper?: string;
  registrationDocumentUrl?: string;
  insuranceDocumentUrl?: string;
};
type FormValues1 = {
  vehicleId: string;
  driverId: string;
  warehousesId: string;
  lastOdometer: string;
  inspectionPaper: string;
};

export type SelectedFilesState = {
  images: boolean;
  imagesOdoMeter: boolean;
  rcDocs: boolean;
  inspectionPaper?: boolean;
  registrationDocumentUrl?: boolean;
  insuranceDocumentUrl?: boolean;
  [key: string]: boolean | undefined;
};

interface FleetVehiclesManagementProps {
  isPaneOpen?: boolean;
  setHeaderActions?: (node: React.ReactNode) => void;
  setHeaderButtons?: (node: React.ReactNode) => void;
}

const FleetVehiclesManagement: React.FC<FleetVehiclesManagementProps> = ({
  isPaneOpen = false,
  setHeaderActions,
  setHeaderButtons,
}) => {
  const { t } = useLanguage();
  const { allowed: canCreateVehicle } = usePermission("vehicles", "create");
  const { allowed: canUpdateVehicle } = usePermission("vehicles", "update");

  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const { refetch, isLoading, isFetching } = useVehicleListData(
    pageNo,
    limit,
    searchFor,
  );
  const { data: cardsSummary } = useFetchFleetCardsSummary();
  const data = useSelector((state: any) => state?.fleet?.data);
  const { mutateAsync: updateVehicleStatus, isPending: activePending } =
    useUpdateVehicleStatus();
  const { mutate: createVehicle, isPending: createPending } =
    useVehicleCreateMutation();
  const { mutate: vehicleAssign, isPending: assignPending } =
    useVehicleAssignMutation();
  const { mutate: updateVehicle, isPending: updatePending } =
    useUpdateVehicleMutation();
  const [vehicleData, setVehicleData] = useState(data);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<FormValues[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);

  useEffect(() => {
    if (data) {
      setVehicleData(data);
      if (selectedVehicle) {
        const updatedVehicle = data.vehicles?.find(
          (v: any) => v.id === selectedVehicle.id,
        );
        if (updatedVehicle) {
          setSelectedVehicle(updatedVehicle);
        }
      }
    }
  }, [data]);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  const debouncedSearchFor = useDebounce(searchFor, 550);

  useEffect(() => {
    refetch();
  }, [debouncedSearchFor, pageNo, limit]);

  // Render stats capsule in the top-right page header
  useEffect(() => {
    if (!setHeaderButtons) return;

    setHeaderButtons(
      <div className="flex items-center flex-wrap gap-2 bg-white/70 backdrop-blur-sm px-4 py-1.5 rounded-xl border border-slate-200 shadow-sm text-[11px] select-none font-bold min-h-8">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">
            <T>Total</T>:
          </span>
          <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
            {cardsSummary?.totalVehicles ?? 0}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-300" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">
            <T>Active</T>:
          </span>
          <span className="text-green-700 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded">
            {cardsSummary?.activeFleet ?? 0} /{" "}
            {cardsSummary?.totalVehicles ?? 0}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-300" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">
            <T>Mileage</T>:
          </span>
          <span className="text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
            {cardsSummary?.totalFleetMileage?.toLocaleString() ?? 0} <T>KM</T>
          </span>
        </div>
        <span className="h-3 w-px bg-slate-300" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">
            <T>Fuel</T>:
          </span>
          <span className="text-rose-700 bg-rose-50 border border-rose-100 px-1.5 py-0.5 rounded">
            SAR{" "}
            {cardsSummary?.totalFuelSpent?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) ?? "0.00"}
          </span>
        </div>
      </div>,
    );

    return () => {
      setHeaderButtons(null);
    };
  }, [setHeaderButtons, cardsSummary]);

  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [modals, setModals] = useState({
    add: false,
    edit: false,
    assign: false,
    view: false,
    confirmStatus: false,
  });

  const toggleModal = (modalName: keyof typeof modals, value?: boolean) => {
    setModals((prev) => ({
      ...prev,
      [modalName]: value !== undefined ? value : !prev[modalName],
    }));
  };

  const [selectedFiles, setSelectedFiles] = useState<SelectedFilesState>({
    images: false,
    imagesOdoMeter: false,
    rcDocs: false,
    inspectionPaper: false,
    registrationDocumentUrl: false,
    insuranceDocumentUrl: false,
  });

  const resetSelectedFiles = () => {
    setSelectedFiles({
      images: false,
      imagesOdoMeter: false,
      rcDocs: false,
      inspectionPaper: false,
      registrationDocumentUrl: false,
      insuranceDocumentUrl: false,
    });
  };

  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdownIndex(null);
      }
    }

    const handleRefetchVehicles = () => {
      refetch();
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener(
      "refetchVehicles",
      handleRefetchVehicles as EventListener,
    );

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener(
        "refetchVehicles",
        handleRefetchVehicles as EventListener,
      );
    };
  }, []);

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<FormValues>();

  const {
    register: assignRegister,
    handleSubmit: handleAssignSubmit,
    clearErrors: clearAssignErrors,
    reset: assignReset,
    setValue: setAssignValue,
    watch: assignWatch,
    control: assignControl,
    formState: { errors: assignErrors },
  } = useForm<FormValues1>();

  const openAddModal = () => toggleModal("add", true);
  const closeAddModal = () => {
    toggleModal("add", false);
    reset();
    resetSelectedFiles();
  };

  const openAssignModal = (vehicle: any) => {
    setSelectedVehicle(vehicle);
    toggleModal("assign", true);
  };
  const closeAssignModal = () => {
    toggleModal("assign", false);
    assignReset();
    setSelectedVehicle(null);
  };

  const openViewModal = (vehicle: any) => {
    window.dispatchEvent(
      new CustomEvent("openVehicleModal", {
        detail: {
          vehicle,
        },
      }),
    );
  };
  const closeViewModal = () => {
    setSelectedVehicle(null);
  };

  const openStatusModal = (vehicle: any) => {
    setSelectedVehicle(vehicle);
    toggleModal("confirmStatus", true);
  };

  const handleUpdateStatus = async () => {
    const newStatus =
      selectedVehicle?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    setVehicleData((prev: any) => ({
      ...prev,
      vehicles: prev.vehicles.map((v: any) =>
        v.id === selectedVehicle?.id ? { ...v, status: newStatus } : v,
      ),
    }));

    try {
      await updateVehicleStatus({ id: selectedVehicle?.id, status: newStatus });

      toast.success(
        newStatus === "ACTIVE"
          ? t("Vehicle status updated to Active")
          : t("Vehicle status updated to Inactive"),
      );
    } catch (error: any) {
      toast.error(t(error?.message || "Failed to update vehicle status"));
      setVehicleData((prev: any) => ({
        ...prev,
        vehicles: prev.vehicles.map((v: any) =>
          v.id === selectedVehicle?.id
            ? { ...v, status: selectedVehicle?.status }
            : v,
        ),
      }));
    } finally {
      toggleModal("confirmStatus", false);
    }
  };

  const handleUpdateStatusDirectly = async (vehicle: any, newStatus: string) => {
    if (!canUpdateVehicle) {
      toast.error(t("You do not have permission to update vehicle status"));
      return;
    }
    setVehicleData((prev: any) => ({
      ...prev,
      vehicles: prev.vehicles?.map((v: any) =>
        v.id === vehicle.id ? { ...v, status: newStatus } : v,
      ) || [],
    }));

    try {
      await updateVehicleStatus({ id: vehicle.id, status: newStatus });
      toast.success(
        t(`Vehicle status updated to ${formatStatus(newStatus)}`)
      );
    } catch (error: any) {
      toast.error(t(error?.message || "Failed to update vehicle status"));
      setVehicleData((prev: any) => ({
        ...prev,
        vehicles: prev.vehicles?.map((v: any) =>
          v.id === vehicle.id ? { ...v, status: vehicle.status } : v,
        ) || [],
      }));
    }
  };

  const handleCreateVehicle = (data: any) => {
    createVehicle(
      { ...data },
      {
        onSuccess: () => {
          closeAddModal();
          refetch();
          toast.success(t("Vehicle Added Successfully."));
        },
        onError: (error) => {
          const errorMessage =
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            (typeof error === "string" ? error : null) ||
            "Failed to Create Vehicle";

          toast.error(errorMessage);
        },
      },
    );
  };

  const handleVehicleAssign = (data: any) => {
    vehicleAssign(
      { ...data, vehicleId: selectedVehicle?.id },
      {
        onSuccess: () => {
          closeAssignModal();
          assignReset();
          refetch();
        },
        onError: (error) => {
          const errorMessage =
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            (typeof error === "string" ? error : null) ||
            "Failed to Assign Vehicle";

          toast.error(errorMessage);
        },
      },
    );
  };

  const [columnsData, setColumnsData] = useState([
    { text: "Checkbox", value: "checkbox", isShow: showCheckboxes },
    { text: "S/N", value: "sn", isShow: true },
    { text: "Vehicle Name", value: "name", isShow: true },
    { text: "RC Number", value: "rc_number", isShow: true },
    { text: "Make & Model", value: "model", isShow: true },
    { text: "Type", value: "vehicle_type", isShow: true },
    { text: "Fuel", value: "fuel_type", isShow: true },
    { text: "Vehicle Number", value: "vehicle_number", isShow: true },
    { text: "Odometer", value: "odometer", isShow: true },
    { text: "Status", value: "status", isShow: true },
    { text: "Assign Status", value: "assignStatus", isShow: true },
    { text: "Actions", value: "actions", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  useEffect(() => {
    if (data?.vehicles) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item) =>
          selectedRows.includes(item.id),
        );
        const newSelected = data.vehicles.filter(
          (s: any) =>
            selectedRows.includes(s.id) &&
            !filteredPrev.some((item) => item.id === s.id),
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [data?.vehicles, selectedRows]);

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === data?.vehicles?.length) {
      setSelectedRows((prev) =>
        prev.filter((id) => !data.vehicles.some((s: any) => s.id === id)),
      );
    } else {
      const newSelections =
        data?.vehicles
          ?.filter((s: any) => !selectedRows.includes(s.id))
          .map((s: any) => s.id) || [];
      setSelectedRows((prev) => [...prev, ...newSelections]);
    }
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "checkbox":
          return {
            label: (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={
                    data?.vehicles?.length > 0 &&
                    data?.vehicles?.every((s: any) =>
                      selectedRows.includes(s.id),
                    )
                  }
                  onChange={handleSelectAll}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                                bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
            render: (s: any) => (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedRows.includes(s.id)}
                  onChange={() => handleSelectRow(s.id)}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                  bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
          };
        case "sn":
          return {
            label: t("S/N"),
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "name":
          return { label: t("Vehicle Name"), render: (c: any) => c.name };
        case "rc_number":
          return { label: t("RC Number"), render: (c: any) => c.rc_number };
        case "model":
          return { label: t("Make & Model"), render: (c: any) => c.model };
        case "vehicle_type":
          return {
            label: t("Type"),
            render: (c: any) => t(formatStatus(c.vehicle_type)),
          };
        case "fuel_type":
          return {
            label: t("Fuel"),
            render: (c: any) => t(formatStatus(c.fuel_type)),
          };
        case "vehicle_number":
          return {
            label: t("Vehicle Number"),
            render: (c: any) => c.vehicle_number,
          };
        case "odometer":
          return {
            label: t("Odometer"),
            render: (c: any) => <div>{c.odometer}.KM</div>,
          };
        case "status":
          return {
            label: t("Status"),
            render: (c: any) => (
              <div className="flex items-center justify-center">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                    getStatusBadgeStyles(c.status || "ACTIVE")
                  }`}
                >
                  {t(formatStatus(c.status || "ACTIVE"))}
                </span>
              </div>
            ),
          };
        case "assignStatus":
          return {
            label: t("Assign Status"),
            render: (c: any) => (
              <div className="flex items-center justify-center">
                <span
                  className={`border text-xs font-medium px-4 py-1.5 rounded-full ${getStatusClass(
                    c?.assignStatus,
                  )}`}
                >
                  {t(formatStatus(c?.assignStatus))}
                </span>
              </div>
            ),
          };
        case "actions":
          return {
            label: t("Actions"),
            render: (s: any) => (
              <div className="relative text-center">
                <button
                  onClick={() =>
                    setOpenDropdownIndex(openDropdownIndex === s ? null : s)
                  }
                  className="p-2 border rounded-full bg-[#F7F6F2]"
                >
                  <IconDotsVertical size={22} />
                </button>

                {openDropdownIndex === s && (
                  <div
                    ref={dropdownRef}
                    className="fixed bg-[#F7F6F2] divide-y text-left z-[999] w-fit whitespace-nowrap rounded-xl shadow-lg overflow-hidden"
                    style={{
                      transform: "translateX(-80%) translateY(-60%)",
                    }}
                  >
                    <div
                      className="px-4 py-2 text-[#404040] hover:bg-[#FBD037] cursor-pointer"
                      onClick={() => openViewModal(s)}
                    >
                      <T>View Details</T>
                    </div>

                    {s.assignStatus === "NOT_ASSIGNED" && (
                      <div
                        onClick={() => openAssignModal(s)}
                        className="px-4 py-2 text-[#404040] hover:bg-[#FBD037] cursor-pointer"
                      >
                        <T>Assign Vehicle</T>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ),
          };
        default:
          return { label: "", render: () => null };
      }
    });

  const mapDataToExport = (c: any, index: number) => ({
    "S/N": index + 1,
    "Vehicle Name": c.name,
    "RC Number": c.rc_number,
    "Make & Model": c.model,
    Type: c.vehicle_type,
    Fuel: c.fuel_type,
    "Vehicle Number": c.vehicle_number,
    Odometer: `${c.odometer} KM`,
    Status: c.status,
    "Assign Status": formatStatus(c.assignStatus),
  });

  return (
    <>
      <div className="space-y-1 ">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1"></div>
          <div className="flex flex-wrap items-center gap-2.5 xl:justify-end w-full xl:w-auto">
            <div className="relative flex items-center">
              <div
                onClick={() => {
                  if (!showSearch) {
                    setShowSearch(true);
                  }
                }}
                className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                  showSearch
                    ? "w-48 sm:w-64 px-2 cursor-default"
                    : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                }`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSearch(!showSearch);
                  }}
                  className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                  title={t("Search")}
                >
                  <IconSearch size={17} stroke={2.5} />
                </button>
                {showSearch && (
                  <>
                    <input
                      type="text"
                      value={searchFor}
                      onChange={(e) => {
                        setSearchFor(e.target.value);
                        setPageNo(1);
                      }}
                      placeholder={t("Search...")}
                      autoFocus
                      className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none focus:outline-none focus:ring-0"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchFor("");
                        setPageNo(1);
                        setShowSearch(false);
                      }}
                      className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                      title={t("Clear & Close")}
                    >
                      <IconX size={14} stroke={2.5} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Download Excel */}
            <button
              onClick={() =>
                handleDownload({
                  selectedRows,
                  allSelectedData,
                  setSelectedRows,
                  setAllSelectedData,
                  showCheckboxes,
                  setShowCheckboxes,
                  setColumnsData,
                  mapDataToExport,
                  fileName: "vehicle_lists.xlsx",
                })
              }
              className={`flex items-center justify-center h-8 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                showCheckboxes && selectedRows.length === 0
                  ? "bg-white/50 text-slate-400"
                  : "bg-white hover:bg-slate-50"
              } ${selectedRows.length > 0 ? "px-3" : "w-8"}`}
              title={t("Download Excel")}
            >
              <IconDownload stroke={2.5} size={17} />
              {selectedRows.length > 0 && (
                <span className="font-bold text-xs ml-1">
                  ({selectedRows.length})
                </span>
              )}
            </button>

            {/* Entries Selector */}
            {vehicleData && vehicleData?.totalPages * limit > 10 && (
              <EntriesSelector
                value={limit}
                options={[
                  10,
                  ...(vehicleData.totalPages * limit > 10 ? [20] : []),
                  ...(vehicleData.totalPages * limit > 20 ? [30] : []),
                  ...(vehicleData.totalPages * limit > 50 ? [50] : []),
                  ...(vehicleData.totalPages * limit > 100 ? [100] : []),
                ]}
                onChange={(val) => {
                  setLimit(val);
                  setPageNo(1);
                }}
              />
            )}

            <button
              className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer flex-shrink-0"
              title={t("Bulk Upload")}
            >
              <IconUpload size={17} stroke={2.5} />
            </button>
            {/* Add Vehicle button */}
            {canCreateVehicle && (
              <button
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("openAddVehicleModal"))
                }
                className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 border border-[#FBD037] bg-[#FBD037] text-[#131327] hover:bg-[#e2bd30] shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer flex-shrink-0"
                title={t("Add Vehicle")}
              >
                <IconPlus size={17} stroke={2.5} />
              </button>
            )}
          </div>
        </div>

        <div className="">
          {isLoading ? (
            <VehicleCardSkeleton rows={6} />
          ) : vehicleData?.vehicles && vehicleData?.vehicles.length > 0 ? (
            <div
              className={`grid grid-cols-1 md:grid-cols-2 ${isPaneOpen ? "xl:grid-cols-3" : "lg:grid-cols-3"} gap-5`}
            >
              {vehicleData.vehicles.map((v: any, idx: number) => (
                <div
                  key={v.id || idx}
                  onClick={() => handleSelectRow(v.id)}
                  className={`group cursor-pointer rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border p-4 transition-all duration-300 space-y-3 relative ${
                    selectedRows.includes(v.id)
                      ? "border-[#FBD037] ring-1 ring-[#FBD037]"
                      : "border-[#D2D2D2]/70"
                  }`}
                >
                  {/* Header */}
                  <div className=" border-b border-[#D2D2D2]/70 pb-2 relative z-10">
                    <div className="flex items-center justify-between">
                      <div></div>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                          getStatusBadgeStyles(v.status || "ACTIVE")
                        }`}
                      >
                        {t(formatStatus(v.status || "ACTIVE"))}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <h3 className="text-[#000000] font-semibold text-lg line-clamp-1">
                        {v.name}
                      </h3>
                      <p className="text-[11px] text-[#212121] tracking-wider">
                        Plate No : {v.vehicle_number || "V-001"}
                      </p>
                    </div>
                  </div>
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

                  {/* Info Section */}
                  <div className="space-y-3 relative z-10">
                    <div className="flex items-center justify-between">
                      <span className="text-[#212121] text-sm">
                        <T>Model</T>:
                      </span>
                      <span className="text-[#212121] text-sm font-medium">
                        {v.model || "-"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-[#212121] text-sm">
                        <T>Vechile Id No</T>:
                      </span>
                      <span className="text-[#212121] text-sm font-medium">
                        {v.rc_number || "-"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-[#212121] text-sm">
                        <T>Vechile Reg Exp date</T>:
                      </span>
                      <span className="text-[#212121] text-sm font-medium">
                        10-06-2028
                      </span>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="flex items-center gap-2 relative z-10">
                    <button
                      className="flex-1 h-10 rounded-lg bg-[#FBD037]/20 text-[#000000] text-sm font-medium hover:bg-[#FBD037]/30 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.dispatchEvent(
                          new CustomEvent("openVehicleModal", {
                            detail: {
                              vehicle: v,
                            },
                          }),
                        );
                      }}
                    >
                      <T>View Details</T>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <NoDataFound />
          )}
        </div>
        <div className="py-4 flex justify-center">
          {vehicleData?.totalPages > 1 && (
            <Pagination
              totalPages={vehicleData?.totalPages}
              currentPage={pageNo}
              fetchData={handlePaginationFetch}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <CommonModal
        title={t("Assign")}
        open={modals.assign}
        closeButtonText={t("Cancel")}
        onClose={closeAssignModal}
        actionButtonText={t("Assign")}
        actionButtonOnClick={handleAssignSubmit(handleVehicleAssign)}
        isPending={assignPending}
      >
        <AssignModal
          register={assignRegister}
          errors={assignErrors}
          setValue={setAssignValue as any}
          watch={assignWatch}
          clearErrors={clearAssignErrors as any}
          control={assignControl}
        />
      </CommonModal>
    </>
  );
};

export default FleetVehiclesManagement;
